import { store } from "../app/store";
import {
  type IBookingReservation,
  type IBookingRoom,
  type IBookingState,
} from "../features/booking/bookingInterface";
import {
  clearBookingReservations,
  clearBookingRooms,
  setBookingError,
  addBookingRoom,
  toggleBookingCartCollapsed,
  addBookingReservations,
  updateBookingRooms,
} from "../features/booking/bookingSlice";
import {
  type IHotelRoomCategory,
  type IHotelRoomCategoryAvailability,
  type IHotelRoomOccupancyAvailabilityPricing,
  type IHotelRate,
} from "../features/hotel/hotelInterface";
import { getReferencePriceForRate } from "./rate";
import { MAX_BOOKABLE_ROOMS } from "../config/booking";
import { getOccupancyData } from "./occupancy";
import { normalizePricing } from "./mewsPrice";

/**
 * Find room category from RoomCategories
 * @param roomCategories
 * @param Id
 * @returns IHotelRoomCategory | undefined
 */
export const findRoomCategory = (Id: IHotelRoomCategory["Id"]): IHotelRoomCategory | undefined => {
  const { hotel } = store.getState();
  return hotel.RoomCategories.find((c) => c.Id === Id);
};

export const getRoomCategoryName = (Id: IHotelRoomCategory["Id"]): string => {
  const { hotel } = store.getState();
  const category = hotel.RoomCategories.find((c) => c.Id === Id);
  if(category !== undefined) return category.Name[hotel.DefaultLanguageCode];
  return '';
};

export const findRoomCategoryByName = (Name: string): IHotelRoomCategory | undefined => {
  const { hotel } = store.getState();
  return hotel.RoomCategories.find((c) => c.Name[hotel.DefaultLanguageCode].trim() === Name);
};

export const filterRoomCategoryAvailability = (RoomCategoryId: IHotelRoomCategory["Id"]) => {
  const { hotel } = store.getState();
  return hotel.RoomCategoryAvailabilities.filter((rca) => rca.RoomCategoryId === RoomCategoryId);
};

export const findRoomCategoryAvailability = (RoomCategoryId: IHotelRoomCategory["Id"]) => {
  const { hotel } = store.getState();
  return hotel.RoomCategoryAvailabilities.find((rca) => rca.RoomCategoryId === RoomCategoryId);
};

export const findRoomOccupancyAvailability = (
  RoomCategoryAvailability: IHotelRoomCategoryAvailability,
  AdultCount: IBookingState["AdultCount"],
  ChildCount: IBookingState["ChildCount"]
) => {
  return RoomCategoryAvailability.RoomOccupancyAvailabilities.find(
    (roa) => roa.AdultCount === AdultCount && roa.ChildCount === ChildCount
  );
};

export const getBookedRoomCount = (
  RoomCategoryId: IHotelRoomCategory["Id"],
  SkipBookedRoomCount = false
) => {
  if (SkipBookedRoomCount) return 0;
  const { booking } = store.getState();
  return booking.Reservations.filter((r) => r.RoomCategoryId === RoomCategoryId).length;
};

export const getAvailableRoomCount = (
  RoomCategoryId: IBookingRoom["RoomCategoryId"],
  SkipBookedRoomCount = false
) => {
  const rca = findRoomCategoryAvailability(RoomCategoryId);
  const bookedRoomCount = getBookedRoomCount(RoomCategoryId, SkipBookedRoomCount);
  return rca && rca.AvailableRoomCount > bookedRoomCount;
};

export const getRoomCategoryIdWithAvailability = (SkipBookedRoomCount = false) => {
  const { hotel, booking } = store.getState();
  if (booking.Error !== null) store.dispatch(setBookingError(null));
  for (let index = 0; index < hotel.RoomCategories.length; index++) {
    const RoomCategoryId = hotel.RoomCategories[index].Id;
    if (getAvailableRoomCount(RoomCategoryId, SkipBookedRoomCount)) {
      return RoomCategoryId;
    }
  }
  return "";
};

export const addRoom = ({
  SkipBookedRoomCount = false,
  ClearRooms = false,
}: {
  SkipBookedRoomCount?: boolean;
  ClearRooms?: boolean;
}): void => {
  const { booking } = store.getState();
  if (booking.Error !== null) store.dispatch(setBookingError(null));
  store.dispatch(clearBookingReservations());
  if (ClearRooms) store.dispatch(clearBookingRooms());

  const roomsAfterClear = ClearRooms ? 0 : booking.Rooms.length;
  if (roomsAfterClear >= MAX_BOOKABLE_ROOMS) {
    store.dispatch(toggleBookingCartCollapsed(false));
    store.dispatch(setBookingError(booking.ErrorText));
    return;
  }

  const RoomCategoryId = getRoomCategoryIdWithAvailability(SkipBookedRoomCount);

  // No availability
  if (RoomCategoryId === "") {
    store.dispatch(toggleBookingCartCollapsed(false));
    store.dispatch(setBookingError(booking.ErrorText));
  }
  // Availability
  else {
    store.dispatch(
      addBookingRoom({
        RoomCategoryId,
        AdultCount: booking.AdultCount,
        ChildCount: booking.ChildCount,
        OccupancyData: getOccupancyData(
          RoomCategoryId,
          booking.AdultCount,
          booking.ChildCount,
        ),
        StartUtc: booking.StartUtc as string,
        EndUtc: booking.EndUtc as string,
      })
    );
  }
};

/**
 * Voucher rates are private rates in the same rate group as the public rate chosen at
 * accommodation. With a voucher, switch to the cheapest private rate of that group when it
 * is actually cheaper; otherwise (or without a voucher) use the public rate of that group.
 */
export const resolveRoomRateId = (
  room: IBookingRoom,
  rates: IHotelRate[],
  availabilities: IHotelRoomCategoryAvailability[],
  currencyCode: string,
  hasVoucher: boolean,
): string | undefined => {
  const rca = availabilities.find((a) => a.RoomCategoryId === room.RoomCategoryId);
  const roa = rca && findRoomOccupancyAvailability(rca, room.AdultCount, room.ChildCount);
  if (!roa) return undefined;

  const rateById = new Map(rates.map((r) => [r.Id, r]));
  const total = (p: IHotelRoomOccupancyAvailabilityPricing) =>
    normalizePricing(p, currencyCode).Price.Total[currencyCode] ?? 0;
  const current = room.RateId ? rateById.get(room.RateId) : undefined;

  if (!current) {
    if (roa.Pricing.some((p) => p.RateId === room.RateId)) return room.RateId;
    return roa.Pricing.find((p) => !rateById.get(p.RateId)?.IsPrivate)?.RateId;
  }

  const groupPricing = roa.Pricing.filter(
    (p) => rateById.get(p.RateId)?.RateGroupId === current.RateGroupId,
  );
  const publicPricing = groupPricing
    .filter((p) => !rateById.get(p.RateId)?.IsPrivate)
    .sort((a, b) => (rateById.get(a.RateId)?.Ordering ?? 0) - (rateById.get(b.RateId)?.Ordering ?? 0));
  const base = current.IsPrivate
    ? publicPricing[0]
    : groupPricing.find((p) => p.RateId === current.Id);

  if (!hasVoucher) return base?.RateId;

  const bestPrivate = groupPricing
    .filter((p) => rateById.get(p.RateId)?.IsPrivate)
    .reduce<IHotelRoomOccupancyAvailabilityPricing | undefined>(
      (best, p) => (best === undefined || total(p) < total(best) ? p : best),
      undefined,
    );
  return bestPrivate && (!base || total(bestPrivate) < total(base))
    ? bestPrivate.RateId
    : base?.RateId;
};

/**
 * True when the voucher gives a discounted private rate for at least one booked room
 */
export const voucherAppliesToRooms = (
  rooms: IBookingRoom[],
  rates: IHotelRate[],
  availabilities: IHotelRoomCategoryAvailability[],
  currencyCode: string,
): boolean => {
  const privateRateIds = new Set(rates.filter((r) => r.IsPrivate).map((r) => r.Id));
  return rooms.some((room) => {
    const rateId = resolveRoomRateId(room, rates, availabilities, currencyCode, true);
    return !!rateId && privateRateIds.has(rateId);
  });
};

export const syncRoomRatesWithVoucher = (): void => {
  const { hotel, booking } = store.getState();
  let changed = false;

  const rooms = booking.Rooms.map((room: IBookingRoom) => {
    const rateId = resolveRoomRateId(
      room,
      hotel.Rates,
      hotel.RoomCategoryAvailabilities,
      hotel.Culture.CurrencyCode,
      booking.VoucherCode !== null,
    );
    if (!rateId || rateId === room.RateId) return room;
    changed = true;
    return { ...room, RateId: rateId };
  });

  if (changed) store.dispatch(updateBookingRooms(rooms));
};

/**
 * Refresh reservations based on rooms
 */
export const refreshReservations = (): void => {
  const { booking } = store.getState();
  store.dispatch(clearBookingReservations());

  let _reservations: IBookingReservation[] = [];

  booking.Rooms.forEach((room: IBookingRoom) => {
    if (room.StartUtc !== booking.StartUtc || room.EndUtc !== booking.EndUtc) return;
    const rca = findRoomCategoryAvailability(room.RoomCategoryId);
    if (rca) {
      const roomOccupancyAvailability = findRoomOccupancyAvailability(
        rca,
        room.AdultCount,
        room.ChildCount
      );
      if (roomOccupancyAvailability) {
        
        const reservation: IBookingReservation = {
          RoomCategoryId: room.RoomCategoryId,
          StartUtc: room.StartUtc as string,
          EndUtc: room.EndUtc as string,
          RateId: room.RateId ?? "",
          AdultCount: room.AdultCount,
          ChildCount: room.ChildCount,
          OccupancyData: roomOccupancyAvailability.OccupancyData,
          ProductIds: room.ProductIds ?? [],
          VoucherCode: booking.VoucherCode,
        };
        _reservations.push(reservation);
      }
    }
  });
  store.dispatch(addBookingReservations(_reservations));
  // store.dispatch(setOpenReservation(_open));
};

export const roomCategoryPriceFrom = ({roomCategoryId}: {roomCategoryId: string}) => {
  const { hotel } = store.getState();
  const rca = findRoomCategoryAvailability(roomCategoryId);
  if (rca) {
    const currencyCode = hotel.Culture.CurrencyCode;
    const totalOf = (p: ReturnType<typeof normalizePricing>) =>
      p.Price.Total[currencyCode] ?? 0;

    // Lowest price over all occupancies and rates, like the calendar
    let best:
      | { pricing: ReturnType<typeof normalizePricing>; occupancyPricing: ReturnType<typeof normalizePricing>[] }
      | undefined;
    rca.RoomOccupancyAvailabilities.forEach((roa) => {
      const occupancyPricing = (roa.Pricing ?? []).map((item) =>
        normalizePricing(item, currencyCode),
      );
      occupancyPricing.forEach((pricing) => {
        if (totalOf(pricing) <= 0) return;
        if (!best || totalOf(pricing) < totalOf(best.pricing)) {
          best = { pricing, occupancyPricing };
        }
      });
    });

    if (best) {
      const { pricing, occupancyPricing } = best;
      const referencePrice = getReferencePriceForRate(
        occupancyPricing,
        pricing.RateId,
        currencyCode,
      );

      return {
        ...pricing,
        ...(referencePrice ? { ReferencePrice: referencePrice } : {}),
      };
    }
  }
  return null;
};

export const isRoomCategoryAvailable = (roomCategoryId: string): boolean => {
  const { hotel } = store.getState();
  const rca = findRoomCategoryAvailability(roomCategoryId);
  const pricing = roomCategoryPriceFrom({ roomCategoryId });
  return (
    (rca?.AvailableRoomCount ?? 0) > 0 &&
    (pricing?.Price?.AveragePerNight?.[hotel.Culture.CurrencyCode] ?? 0) > 0
  );
};

